import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CardContent,
  CardMedia,
  Typography,
  Pagination,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Article } from '../types';
import { formatDateTimeFr } from '../methods';
import LoadingProgress from './LoadingProgress';
import { toast } from 'react-toastify';

const Articles: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6); // par défaut 6
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');

  const navigate = useNavigate();

  const fetchArticles = () => {
    setLoading(true);
    api
      .get('/articles/paginated', {
        params: {
          page,
          limit,
          criteria: search.trim() || undefined,
        },
      })
      .then((res) => {
        setArticles(res.data.data);
        setTotalPages(res.data.totalPages);

      })
      .catch((err) => {

        console.log(err)
        toast.error("Erreur lors de la récupération des données : " + err.message)
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);


  const handleLimitChange = (e: any) => {
    setLimit(e.target.value);
    setPage(1); // Reset to first page when changing limit
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1); // Reset page
    fetchArticles();
  };

  const handleAddArticle = () => {
    navigate('/articles/add');
  };



  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1}>
        <Typography variant="h5">Articles</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddArticle}
        >
          Add Article
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSearchSubmit} mb={3} display="flex" gap={2}>
        <TextField
          label="Rechercher par ID, description, tag ou lien"
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleSearchChange}
        />
        <Button type="submit" variant="contained" color="primary" >
          Rechercher
        </Button>
      </Box>

      <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Items/page</InputLabel>
          <Select
            value={limit}
            label="Items/page"
            onChange={handleLimitChange}
          >
            <MenuItem value={3}>3</MenuItem>
            <MenuItem value={6}>6</MenuItem>
            <MenuItem value={9}>9</MenuItem>
            <MenuItem value={12}>12</MenuItem>
            <MenuItem value={12}>15</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="20vh"
          >
            <CircularProgress />
          </Box>
        ) : (
          articles.length === 0 ? (
            <Typography>No articles found.</Typography>
          ) : (
            <>
              <Box display="flex" flexWrap="wrap" mb={4} gap={2}>
                {articles.map((article) => (
                  <Box
                    key={article.id}
                    onClick={() => navigate(`/articles/${article.id}`)}
                    sx={{
                      width: { xs: '100%', sm: '48%', md: '32%' },
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #ddd',
                      borderRadius: 2,
                      boxShadow: 2,
                      overflow: 'hidden',
                      minHeight: 350,
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 180,
                        width: '100%',
                        borderBottom: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa',
                      }}
                    >
                      {article.image ? (
                        <CardMedia
                          component="img"
                          image={article.image}
                          alt="Article"
                          sx={{
                            maxHeight: '100%',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            p: 1,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </Box>

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        ID : {article.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Description : {article.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type : {article.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Tag : {article.tag || ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Link :{' '}
                        <a href={article.link} target="_blank" rel="noopener noreferrer">
                          open
                        </a>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Modified at : {formatDateTimeFr(article.modified_at)}
                      </Typography>
                    </CardContent>
                  </Box>
                ))}
              </Box>

              <Box display="flex" justifyContent="center" mb={4}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            </>
          )
        )}

      </>
    </Box>
  );
};

export default Articles;
